import { Instrument } from '../../../types/shared'
import { SpecificChartFunctionality } from '../../constants/common'
import { clip, getSettings, recountScale } from '../../controllers/renderUtils'
import { getContextWithCache } from '../../lib/shims'
import ChartLayoutModel from '../../models/chart_layout'
import { ISettings } from '../../models/chart_settings/interfaces'
import { getChartLayoutSettings } from '../../models/settings'
import { getChartLayoutPositions } from './getChartLayoutPositions'
import { LOGO_BAR_HEIGHT, renderLogoAndDate } from './renderLogoAndDate'
import { renderTickerTimeframeChange } from './renderTickerTimeframeChange'

async function renderChartsToCanvas({
  chartLayoutModel,
  scale,
  canvas,
  canvasContext,
}: {
  chartLayoutModel: ChartLayoutModel
  scale: number
  canvas: HTMLCanvasElement
  canvasContext: CanvasRenderingContext2D
}) {
  const { ChartSettings } = getChartLayoutSettings(chartLayoutModel)

  canvas.width = chartLayoutModel.width * scale
  canvas.height = chartLayoutModel.height + LOGO_BAR_HEIGHT * scale
  canvasContext.scale(scale, scale)

  canvasContext.set('fillStyle', 'white')
  canvasContext.fillRect(0, 0, canvas.width, canvas.height)

  const charts = chartLayoutModel.charts().all()
  const positions = getChartLayoutPositions(chartLayoutModel)

  await renderLogoAndDate({ canvasContext, chartLayoutModel, width: canvas.width })
  canvasContext.translate(0, LOGO_BAR_HEIGHT)
  canvasContext.set('fillStyle', ChartSettings.general.Colors.canvasFill)
  canvasContext.fillRect(0, 0, canvas.width, canvas.height - LOGO_BAR_HEIGHT)

  for (let i = 0; i < charts.length; i++) {
    let paneYOffset = 0
    const { x, y } = positions[i]
    canvasContext.translate(x, y)
    if (chartLayoutModel.specificChartFunctionality === SpecificChartFunctionality.chartPage) {
      renderTickerTimeframeChange({ chartLayoutModel, canvasContext, chartModel: charts[i] })
    }

    for (const pane of charts[i].panes().all()) {
      const roundedPaneHeight = Math.round(pane.height)
      let Settings
      canvasContext.save()
      canvasContext.translate(0, paneYOffset)
      recountScale({ chartModel: charts[i], paneModel: pane })

      if (charts[i].instrument === Instrument.MarketSentiment) {
        canvasContext.clearRect(0, 0, charts[i].width, roundedPaneHeight)
      } else {
        canvasContext.set('fillStyle', ChartSettings.general.Colors.canvasFill)
        canvasContext.fillRect(0, 0, charts[i].width, roundedPaneHeight)
      }

      for (const element of pane.elements().all()) {
        const el = element.instance
        if (el.renderGrid != null) {
          canvasContext.save()
          el.renderGrid(canvasContext)
          canvasContext.restore()
        }
        canvasContext.save()
        if (el.renderContent != null) {
          if (!Settings) {
            Settings = getSettings(pane)
          }
          canvasContext.save()
          clip({
            canvasCtx: canvasContext,
            width: charts[i].width,
            height: roundedPaneHeight,
            Settings: Settings as ISettings['ChartSettings'],
          })
          canvasContext.translate(charts[i].leftOffset + Settings.left.width, Settings.top.height)
          el.renderContent(canvasContext)
          canvasContext.restore()
          if (typeof el.renderLabels === 'function') {
            el.renderLabels(canvasContext)
          }
        } else {
          el.render!(canvasContext)
        }
        if (
          chartLayoutModel.specificChartFunctionality === SpecificChartFunctionality.chartPage &&
          el.renderCross != null
        ) {
          el.renderCross(canvasContext)
        }
        canvasContext.restore()
      }

      canvasContext.restore()
      paneYOffset += roundedPaneHeight
    }
    canvasContext.translate(x * -1, y * -1)
  }

  return canvas
}

export async function chartLayoutToImg(model: ChartLayoutModel) {
  const canvas = document.createElement('canvas')
  const canvasContext = getContextWithCache(canvas)
  if (!canvasContext) return null

  const result = await renderChartsToCanvas({
    chartLayoutModel: model,
    scale: 1,
    canvas,
    canvasContext,
  })

  return result.toDataURL()
}
